import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { getShiftDefinitions } from "../../apis/shiftDefinition";
import { formatTimeToAmPm } from "../../utils/DateUtil";
import { getClinicUsers } from "../../apis/user";
import { getDepartments } from "../../apis/departments";
import { createShiftAssignment } from "../../apis/shiftAssignment";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

interface AssignShiftModalProps {
  onClose: () => void;
  onAssigned: () => void;
}

const getDatesInRange = (start: string, end: string, selectedDays: number[]): string[] => {
  const dates: string[] = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  const current = new Date(startDate);
  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    if (selectedDays.includes(dayOfWeek)) {
      dates.push(current.toISOString().slice(0, 10));
    }
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const AssignShiftModal: React.FC<AssignShiftModalProps> = ({ onClose, onAssigned }) => {
  const { showNotification } = useNotification();
  const [shifts, setShifts] = useState<any[]>([]);
  const [clinicUsers, setClinicUsers] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedClinicUserId, setSelectedClinicUserId] = useState<number | null>(null);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [location, setLocation] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon–Fri default
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getShiftDefinitions({ per_page: 100 }).then((res: any) => {
      setShifts(res.data?.data || res.data || []);
    });
    getClinicUsers().then((res: any) => {
      const data = res?.data ?? res;
      setClinicUsers(Array.isArray(data) ? data : data?.data ?? []);
    });
    getDepartments().then((res: any) => {
      const data = res?.data ?? res;
      setDepartments(Array.isArray(data) ? data : data?.data ?? []);
    });
  }, []);

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const selectAllDays = () => setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  const clearAllDays = () => setSelectedDays([]);

  const handleAssign = async () => {
    if (!selectedClinicUserId || !selectedShiftId || !selectedDepartmentId) {
      showNotification("warning", "Please select clinic user, shift, and department", "", "topRight");
      return;
    }
    if (startDate > endDate) {
      showNotification("warning", "End date must be on or after start date", "", "topRight");
      return;
    }
    if (selectedDays.length === 0) {
      showNotification("warning", "Please select at least one day of the week", "", "topRight");
      return;
    }
    const dates = getDatesInRange(startDate, endDate, selectedDays);
    if (dates.length === 0) {
      showNotification("warning", "No dates match the selected range and days", "", "topRight");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        dates.map((date) =>
          createShiftAssignment({
            clinicUserId: selectedClinicUserId,
            shiftDefinitionId: selectedShiftId,
            departmentId: selectedDepartmentId,
            location: location.trim() || undefined,
            date,
          })
        )
      );
      showNotification("success", `${dates.length} shift(s) assigned successfully`, "", "topRight");
      onAssigned();
      onClose();
    } catch (err: any) {
      showNotification("error", "Failed to assign shift(s)", err?.response?.data?.message || "", "topRight");
    } finally {
      setSaving(false);
    }
  };

  const datesToCreate = getDatesInRange(startDate, endDate, selectedDays);

  const getClinicUserLabel = (u: any) => {
    const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
    return name || u.email || `Clinic User #${u.id}`;
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Clinic User</label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select clinic user"
          value={selectedClinicUserId ?? undefined}
          onChange={(v) => setSelectedClinicUserId(v)}
          options={clinicUsers.map((u) => ({
            value: u.id,
            label: getClinicUserLabel(u),
          }))}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select shift"
          value={selectedShiftId ?? undefined}
          onChange={(v) => setSelectedShiftId(v)}
          options={shifts.map((s) => ({
            value: s.id,
            label: `${s.name} (${formatTimeToAmPm(s.startTime ?? "")} - ${formatTimeToAmPm(s.endTime ?? "")})`,
          }))}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select department"
          value={selectedDepartmentId ?? undefined}
          onChange={(v) => setSelectedDepartmentId(v)}
          options={departments.map((d) => ({
            value: d.id,
            label: d.name ?? d.code ?? `Department #${d.id}`,
          }))}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. OPD, Ward A"
          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">Days of Week</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAllDays}
              className="text-xs text-primary hover:underline"
            >
              All
            </button>
            <button
              type="button"
              onClick={clearAllDays}
              className="text-xs text-gray-500 hover:underline"
            >
              None
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {DAYS_OF_WEEK.map(({ value, label }) => (
            <label
              key={value}
              className={`inline-flex items-center px-3 py-1.5 rounded-md border cursor-pointer text-sm ${
                selectedDays.includes(value)
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-gray-300 bg-white text-gray-600 hover:border-gray-400"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedDays.includes(value)}
                onChange={() => toggleDay(value)}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      {datesToCreate.length > 0 && (
        <p className="text-xs text-gray-500">
          {datesToCreate.length} assignment(s) will be created for the selected range and days.
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleAssign}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Assigning..." : "Assign"}
        </button>
      </div>
    </div>
  );
};

export default AssignShiftModal;
